import { useState, useCallback, useRef, useEffect } from 'react';
import { InvoiceItem } from '../../types/invoiceTypes'; // Adjust path if necessary
import { ProductData } from '../../types/offerTypes'; // Need ProductData type
import { useAuth } from '../../context/AuthContext'; // Adjust path if necessary
import { db } from '../../firebaseConfig'; // Adjust path if necessary
import { collection, doc, getDoc, getDocs, query as firestoreQuery, where } from 'firebase/firestore'; // Nur Lesezugriff für Duplikat-/Änderungsprüfung
import logger, { nativeConsole } from '../../utils/logger'; // Adjust path if necessary
// import { deepEqual } from '../../utils/objectUtils'; // deepEqual might not be needed if comparing specific fields

// **FIX 16: Dialog-Manager für Product Management (reuse from Customer Management)**
declare let globalDialogManager: {
  isActive: boolean;
  type: 'customer' | 'product' | null;
  priority: number;
};

// Dialog Lock Management
const dialogLocks = new Map<string, { priority: number; timestamp: number }>();

function acquireDialogLock(type: 'customer' | 'product', priority: number = 1): boolean {
  const now = Date.now();
  
  // Check if any lock exists with higher or equal priority
  const entries = Array.from(dialogLocks.entries());
  for (let i = 0; i < entries.length; i++) {
    const [lockType, lock] = entries[i];
    // Skip if it's the same type (we can update our own lock)
    if (lockType === type) continue;
    
    // If another lock exists with higher or equal priority, deny
    if (lock.priority >= priority) {
      // Check if lock is stale (older than 30 seconds)
      if (now - lock.timestamp > 30000) {
        // Remove stale lock
        dialogLocks.delete(lockType);
      } else {
        return false;
      }
    }
  }
  
  // Acquire or update the lock
  dialogLocks.set(type, { priority, timestamp: now });
  return true;
}

function releaseDialogLock(type: 'customer' | 'product'): void {
  dialogLocks.delete(type);
}

// Exported type for the hook's return value
export interface ProductManagementResult {
  productMetaForItems: { [itemId: string]: {
    action: 'create' | 'update' | 'none';
    productId: string | null;
  }};
  checkAndHandleProductUpdate: (currentItems: InvoiceItem[]) => Promise<boolean | 'continue'>;
  handleProductSelection: (itemId: string, selectedProduct: ProductData) => void;
  resetProductManagement: () => void; // Function to reset state
  isLoading: boolean;
  error: string | null;
  // Dialog-related properties
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  dialogConfig: {
    title: string;
    message: string;
    action: 'create' | 'update' | 'none' | 'process';
    items: InvoiceItem[];
    itemsToProcess: Array<{
      action: 'create' | 'update';
      item: InvoiceItem;
      productId?: string;
      originalProduct?: ProductData;
    }>;
  };
  handleDialogAction: (action: string) => Promise<'continue' | 'abort'>;
  closeDialogAndContinue: () => void;
  // **FIX: Funktion zum Abrufen der aktuellen productMetaForItems (löst async State-Problem)**
  getProductMetaForItems: () => { [itemId: string]: { action: 'create' | 'update' | 'none'; productId: string | null } };
}

// Comparison logic moved and refined
const compareProductData = (
  currentItem: InvoiceItem,
  originalProduct: ProductData | null
): { needsUpdate: boolean; needsCreate: boolean } => {
  if (!originalProduct || !originalProduct.productId) {
    // If no original or original is invalid, assume creation ONLY if current item has a name
    return { needsUpdate: false, needsCreate: !!currentItem.name };
  }

  // --- Check 1: Identifier Change (Suggests NEW Product) ---
  // If the core identifier (e.g., name) changes, it's conceptually a different product.
  const identifierChanged =
    (currentItem.name || '') !== (originalProduct.name || '');
    // You could add sellerArticleId here IF changing it should also mean creating a new product.
    // || (currentItem.sellerArticleId || null) !== (originalProduct.sellerArticleId || null);

  if (identifierChanged) {
    nativeConsole.log(`Product identifier changed ('${originalProduct.name}' -> '${currentItem.name}') for item ${currentItem.id}. Suggesting CREATE new.`);
    // Only suggest create if the *current* item actually has a name
    return { needsUpdate: false, needsCreate: !!currentItem.name };
  }

  // --- Check 2: Data Change (Suggests UPDATE Existing Product) ---
  // Compare other relevant fields. Exclude invoice-specific fields (quantity, prices, tax).
  const dataChanged =
    (currentItem.description || '') !== (originalProduct.description || '') ||
    (currentItem.unitCode || null) !== (originalProduct.unitCode || null) ||
    (currentItem.sellerArticleId || null) !== (originalProduct.sellerArticleId || null) ||
    (currentItem.buyerArticleId || null) !== (originalProduct.buyerArticleId || null);
    // Add other fields from ProductData that should trigger an update if changed in InvoiceItem

  if (dataChanged) {
    nativeConsole.log(`Product data changed for item ${currentItem.id} (Product ID: ${originalProduct.productId}). Suggesting UPDATE.`);
    return { needsUpdate: true, needsCreate: false };
  }

  // --- No Change ---
  nativeConsole.log(`No significant product data changes detected for item ${currentItem.id} (Product ID: ${originalProduct.productId}).`);
  return { needsUpdate: false, needsCreate: false };
};


export const useProductManagement = (): ProductManagementResult => {
  const { currentUser, effectiveScopeUid } = useAuth();

  // **FIX 17: Verbesserte State-Verwaltung mit Refs**
  const [originalProducts, setOriginalProducts] = useState<{ [itemId: string]: ProductData | null }>({});
  const [productMetaForItems, setProductMetaForItems] = useState<{ [itemId: string]: { action: 'create' | 'update' | 'none', productId: string | null } }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // **FIX: Ref für sofortigen Zugriff auf productMetaForItems (löst async State-Problem)**
  const productMetaRef = useRef<{ [itemId: string]: { action: 'create' | 'update' | 'none', productId: string | null } }>({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogConfig, setDialogConfig] = useState<{
    title: string;
    message: string;
    action: 'create' | 'update' | 'none' | 'process';
    items: InvoiceItem[];
    itemsToProcess: Array<{
      action: 'create' | 'update';
      item: InvoiceItem;
      productId?: string;
      originalProduct?: ProductData;
    }>;
  }>({
    title: '',
    message: '',
    action: 'none',
    items: [],
    itemsToProcess: []
  });

  // **FIX 18: Refs für sichere Async-Operationen**
  const isMountedRef = useRef(true);
  const dialogLockRef = useRef<boolean>(false);

  // **FIX 19: Verbessertes Mounting/Cleanup-Management**
  useEffect(() => {
    // Setze beim Mount/Re-Mount auf true
    isMountedRef.current = true;
    // **FIX: Initialisiere productMetaRef mit aktuellem State-Wert**
    productMetaRef.current = productMetaForItems;
    
    return () => {
      // Cleanup beim Unmount
      isMountedRef.current = false;
      if (dialogLockRef.current) {
        releaseDialogLock('product');
        dialogLockRef.current = false;
      }
    };
  }, [productMetaForItems]);
  
  // **FIX: Sichere setProductMetaForItems Funktion die auch Ref aktualisiert**
  const setProductMetaSafe = useCallback((newMeta: { [itemId: string]: { action: 'create' | 'update' | 'none', productId: string | null } }) => {
    nativeConsole.log('Setting productMetaForItems:', newMeta);
    setProductMetaForItems(newMeta);
    productMetaRef.current = newMeta;
  }, []);
  
  // **FIX: Funktion zum Abrufen der aktuellen productMetaForItems aus der Ref**
  const getProductMetaForItems = useCallback(() => {
    nativeConsole.log('getProductMetaForItems called - returning:', productMetaRef.current);
    return productMetaRef.current;
  }, []);

  // **FIX 20: Sichere Dialog-Öffnung**
  const openDialog = useCallback((config: any): boolean => {
    if (!isMountedRef.current) {
      return false;
    }

    // Versuche Dialog-Lock zu erwerben
    if (!acquireDialogLock('product', 2)) { // Higher priority than customer
      nativeConsole.warn('Product dialog blocked - another dialog is active');
      return false;
    }

    dialogLockRef.current = true;
    setDialogConfig(config);
    setDialogOpen(true);
    nativeConsole.log('Product dialog opened successfully');
    return true;
  }, []);

  // **FIX 21: Sichere Dialog-Schließung**
  const closeDialog = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    setDialogOpen(false);
    if (dialogLockRef.current) {
      releaseDialogLock('product');
      dialogLockRef.current = false;
    }
    nativeConsole.log('Product dialog closed and lock released');
  }, []);

  // **FIX 22: Verbesserter Reset mit Lock-Management**
  const resetProductManagement = useCallback(() => {
    if (!isMountedRef.current) return;
    
    // Änderung: Berücksichtige den Dialog-Status beim Reset
    if (dialogOpen) {
      // Wenn ein Dialog aktiv ist, nur teilweisen Reset durchführen
      nativeConsole.log("Produktverwaltung: Teilweiser Reset (Dialog ist aktiv)");
      setIsLoading(false);
      setError(null);
      // Dialog und produktbezogene Daten werden beibehalten
    } else {
      // Vollständiger Reset, wenn kein Dialog aktiv ist
      nativeConsole.log("Product management state reset.");
      setProductMetaForItems({});
      productMetaRef.current = {}; // **FIX: Auch Ref zurücksetzen**
      setOriginalProducts({});
      closeDialog();
      setIsLoading(false);
      setError(null);
    }
  }, [dialogOpen, closeDialog]);

  // **FIX 23: Sichere Produkt-Selection**
  const handleProductSelection = useCallback((itemId: string, selectedProduct: ProductData) => {
    if (!isMountedRef.current) return;
    
    if (!itemId || !selectedProduct) {
        nativeConsole.warn("handleProductSelection called with invalid arguments:", { itemId, selectedProduct });
        return;
    }
    
    // Validiere Produkt-ID, um sicherzustellen, dass sie eine gültige Firestore-Dokument-ID ist
    const productId = selectedProduct.productId || null;
    const isValidProductId = productId && !productId.includes('||') && productId.length > 5;
    
    nativeConsole.log(`Product selected in hook for item ID=${itemId}, Product ID=${productId}:`, 
                       selectedProduct, `Valid ID: ${isValidProductId}`);
    
    // Store a deep copy of the original product data
    setOriginalProducts(prev => ({
      ...prev,
      [itemId]: JSON.parse(JSON.stringify(selectedProduct))
    }));
    
    // Initialize meta state with validated product ID
    // **FIX: Auch Ref aktualisieren für sofortigen Zugriff**
    const newMeta = {
      ...productMetaRef.current,
      [itemId]: { 
        action: 'none' as const, 
        productId: isValidProductId ? productId : null 
      }
    };
    setProductMetaForItems(newMeta);
    productMetaRef.current = newMeta;
    
    setError(null); // Clear error on new selection

    // NOTE: lastUsed timestamp wird NICHT hier aktualisiert, sondern erst 
    // bei der tatsächlichen Rechnungserstellung im Backend (InvoiceController.php)
    // Das macht mehr Sinn, da ein Produkt nur dann als "verwendet" gelten sollte,
    // wenn es tatsächlich in einer gespeicherten Rechnung steht.
  }, [currentUser]);


  // --- Database Operations ---

  // Mark product for update (actual DB operation happens in backend)
  const updateProduct = useCallback(async (productId: string, itemData: InvoiceItem): Promise<boolean> => {
    if (!currentUser || !productId) {
      setError("Update failed: User not logged in or Product ID missing.");
      nativeConsole.error("Update product precondition failed.", { currentUser: !!currentUser, productId });
      return false;
    }

    // **FIX: Keine direkte Firestore-Speicherung mehr im Frontend!
    // Das Backend übernimmt die Speicherung - wir setzen nur die Absicht (action: 'update')**
    nativeConsole.log(`Produkt ${productId} wird für Update markiert (Backend übernimmt Speicherung)`);

    // Update internal 'originalProducts' state
    setOriginalProducts(prev => {
      const currentOriginal = prev[itemData.id];
      const updatedOriginal = { ...(currentOriginal || {}), productId: productId };
      return { ...prev, [itemData.id]: updatedOriginal as ProductData };
    });
    
    // Setze action auf 'update' - das Backend wird die eigentliche Speicherung durchführen
    const newMeta = { ...productMetaRef.current, [itemData.id]: { action: 'update' as const, productId: productId } };
    setProductMetaForItems(newMeta);
    productMetaRef.current = newMeta;

    return true;
  }, [currentUser]);


  // Mark product for creation (actual DB operation happens in backend)
  const createProduct = useCallback(async (itemData: InvoiceItem): Promise<string | null> => {
    if (!currentUser) {
      setError("Create failed: User not logged in.");
      nativeConsole.error("Create product precondition failed.", { currentUser: !!currentUser });
      return null;
    }
    if (!itemData.name) {
        setError("Create failed: Product name is required.");
        nativeConsole.error("Create product precondition failed: Missing name.", { itemData });
        return null;
    }

    // Setze action auf 'create' - das Backend wird die eigentliche Speicherung durchführen
    // productId ist null, da das Produkt noch nicht existiert
    const newMeta = { ...productMetaRef.current, [itemData.id]: { action: 'create' as const, productId: null } };
    setProductMetaForItems(newMeta);
    productMetaRef.current = newMeta;
    nativeConsole.log("Product marked for creation - Backend wird speichern");

    return 'pending-backend-creation'; // Marker dass Backend erstellen soll
  }, [currentUser]);

  // Dialog action handler
  const handleDialogAction = useCallback(async (action: string): Promise<'continue' | 'abort'> => {
    // Wenn Abbrechen oder 'cancel' gewählt wird, den Dialog schließen und mit dem Prozess fortfahren
    if (action === 'cancel') {
      closeDialog();
      return 'continue'; // Prozess fortsetzen
    }
    
    if (action === 'process') {
      let success = true;
      
      // Process all items that need action
      for (const { action: itemAction, item, productId } of dialogConfig.itemsToProcess) {
        let opSuccess = false;
        
        if (itemAction === 'update' && productId) {
          nativeConsole.log(`Executing update for product ID: ${productId} using item: ${item.name}`);
          opSuccess = await updateProduct(productId, item);
        } else if (itemAction === 'create') {
          nativeConsole.log(`Executing creation for item: ${item.name}`);
          const newId = await createProduct(item);
          opSuccess = !!newId;
        }
        
        if (!opSuccess) {
          success = false;
          nativeConsole.error(`Database operation ${itemAction} failed for item ${item.id}.`);
        }
      }
      
      if (!success) {
        setError("Einige Produktaktualisierungen oder -erstellungen sind fehlgeschlagen.");
      }
      
      closeDialog();
      return 'continue'; // Prozess fortsetzen
    }
    
    // Standardfall
    closeDialog();
    return 'abort'; // Prozess abbrechen
  }, [dialogConfig, updateProduct, createProduct, closeDialog]);

  // Main function to check all items and prepare dialog
  const checkAndHandleProductUpdate = useCallback(async (currentItems: InvoiceItem[]): Promise<boolean | 'continue'> => {
    if (!currentUser) {
        nativeConsole.warn("checkAndHandleProductUpdate skipped: No user logged in.");
        return false; // Kein Dialog wurde geöffnet
    }

    // Wenn keine Items vorhanden sind oder alle Items neu (ohne Namen) sind, 
    // brauchen wir keinen Dialog zu zeigen
    if (currentItems.length === 0 || currentItems.every(item => !item.name)) {
        return true; // Validation erfolgreich - Prozess fortsetzen
    }

    nativeConsole.log("Starting product check and update process...");
    setError(null); // Clear previous errors

    const updatedMeta: { [itemId: string]: { action: 'create' | 'update' | 'none', productId: string | null } } = {};
    const itemsToProcess: Array<{ 
      action: 'create' | 'update'; 
      item: InvoiceItem; 
      productId?: string;
      originalProduct?: ProductData;
    }> = [];

    // Überprüfe jedes Item auf Änderungen
    for (const item of currentItems) {
        if (!item || !item.id) {
            nativeConsole.warn("Skipping invalid item:", item);
            continue;
        }

        // Ignoriere leere Items oder Items ohne Namen/Preis
        if (!item.name || !item.netPrice || item.netPrice <= 0) {
            nativeConsole.log(`Überspringe unvollständiges Item ${item.id}:`, {
                name: item.name,
                price: item.netPrice
            });
            continue;
        }

        // Hole die aktuelle Produkt-ID aus den Meta-Daten
        const currentProductId = productMetaForItems[item.id]?.productId || null;
        let determinedAction: 'create' | 'update' | 'none' = 'none';
        let productIdForAction: string | null = currentProductId;
        let originalProductData: ProductData | undefined = undefined;

        // NEUE PRÜFUNG: Gibt es ein Original-Produkt im State?
        const originalInState = originalProducts[item.id];
        const hasOriginalInState = !!originalInState;
        
        // Verbesserte Logik zur Erkennung bereits existierender Produkte
        // Wenn wir eine Produkt-ID haben, ist das Produkt aus der Datenbank und kein neues
        if (currentProductId) {
            nativeConsole.log(`Item ${item.id} hat eine gültige Produkt-ID (${currentProductId}), überprüfe auf Änderungen`);
            
            try {
                // Korrekter Pfad für flache Struktur: products/{productId}
                const docRef = doc(db, 'products', currentProductId);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                    const productData = docSnap.data() as ProductData;
                    originalProductData = productData;
                    
                    // Detailliertes Logging für bessere Diagnose
                    nativeConsole.log(`Vergleiche Item mit bestehendem Produkt (Pfad: products/${currentProductId}):`, {
                        itemName: item.name,
                        itemPrice: item.netPrice,
                        productName: productData.name,
                        productPrice: productData.netPrice,
                        productId: currentProductId
                    });

                    // VERBESSERTE Prüfung mit genaueren Toleranzen
                    // Wir vergleichen nur Felder, die tatsächlich vom Benutzer geändert werden können
                    // und ignorieren kleine Rundungsunterschiede bei Zahlen
                    const nameChanged = item.name !== productData.name;
                    
                    // Erhöhe Toleranz bei Preisvergleichen mit präziserer Rundung
                    const priceRounded = Math.round((item.netPrice || 0) * 100) / 100;
                    const originalPriceRounded = Math.round((productData.netPrice || 0) * 100) / 100;
                    const priceChanged = Math.abs(priceRounded - originalPriceRounded) > 0.01;
                    
                    const taxRateRounded = Math.round((item.taxRate || 0) * 100) / 100;
                    const originalTaxRateRounded = Math.round((productData.taxRate || 0) * 100) / 100;
                    const taxRateChanged = Math.abs(taxRateRounded - originalTaxRateRounded) > 0.1; // 0.1 Prozentpunkt Toleranz
                    
                    // Berücksichtige Leerzeichen und null/undefiniert als gleichwertig bei Beschreibungen
                    const itemDesc = (item.description || '').trim();
                    const productDesc = (productData.description || '').trim();
                    const descriptionChanged = itemDesc !== productDesc && (itemDesc !== '' || productDesc !== '');

                    const hasSignificantChanges = nameChanged || priceChanged || taxRateChanged || descriptionChanged;

                    if (hasSignificantChanges) {
                        // Wenn es Änderungen gibt, schlagen wir Update vor
                        determinedAction = 'update';
                        productIdForAction = currentProductId;
                        
                        nativeConsole.log(`Signifikante Änderungen am Produkt ${currentProductId} erkannt:`, {
                            nameChanged,
                            priceChanged,
                            taxRateChanged,
                            descriptionChanged,
                            originalName: productData.name,
                            newName: item.name,
                            originalPrice: originalPriceRounded,
                            newPrice: priceRounded
                        });
                        
                        itemsToProcess.push({
                            action: 'update',
                            item,
                            productId: currentProductId,
                            originalProduct: productData
                        });
                    } else {
                        // Keine Änderungen - behalte einfach die Referenz auf das bestehende Produkt
                        determinedAction = 'none';
                        productIdForAction = currentProductId;
                        nativeConsole.log(`Keine Änderungen am Produkt ${currentProductId} erkannt`);
                    }
                } else {
                    // Falls Produkt nicht mehr existiert (seltener Fall), als neues behandeln
                    nativeConsole.warn(`Ausgewähltes Produkt ${currentProductId} nicht in der Datenbank gefunden (Pfad: products/${currentProductId})`);
                    determinedAction = 'create';
                    productIdForAction = null;
                    
                    itemsToProcess.push({
                        action: 'create',
                        item
                    });
                }
            } catch (error) {
                nativeConsole.error(`Fehler beim Prüfen des Produkts ${currentProductId}:`, error);
                // Im Fehlerfall konservativ handeln und nichts ändern
                determinedAction = 'none';
                productIdForAction = currentProductId;
            }
        } 
        else if (item.name && item.netPrice > 0) {
            // KRITISCH: Prüfe, ob das Item aus Vorschlägen stammt
            // Ein Produkt kann von der Datenbank kommen AUCH WENN currentProductId noch nicht gesetzt wurde,
            // wenn der Benutzer es gerade aus der Autovervollständigung ausgewählt hat
            
            // Prüfe ob in originalProducts existiert (was bedeutet, dass es aus den Vorschlägen kommt)
            if (hasOriginalInState && originalInState?.productId) {
                // Dies ist ein existierendes Produkt aus der Autovervollständigung
                nativeConsole.log(`Item ${item.id} wurde aus Vorschlägen ausgewählt, verwende vorhandene Produkt-ID:`, {
                    productId: originalInState.productId,
                    productName: originalInState.name
                });
                
                determinedAction = 'none';
                productIdForAction = originalInState.productId;
            } 
            else {
                // Nur dann ist es wirklich ein neues Produkt, wenn es keine Verbindung zur Datenbank hat
                nativeConsole.log(`Prüfe, ob Item ${item.id} wirklich neu ist:`, { 
                    name: item.name, 
                    price: item.netPrice, 
                    hasOriginalInState: hasOriginalInState 
                });
                
                // Nochmals prüfen, ob es eventuell ein existierendes Produkt mit gleichem Namen gibt
                try {
                    const productsCollectionRef = collection(db, 'products');
                    const q = firestoreQuery(productsCollectionRef, 
                        where('userId', '==', effectiveScopeUid),
                        where('name', '==', item.name)
                    );
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                        // Ein ähnliches Produkt existiert bereits, vorschlagen dieses zu verwenden
                        const existingProductDoc = querySnapshot.docs[0];
                        const existingProductData = existingProductDoc.data() as ProductData;
                        
                        nativeConsole.log(`Produkt mit gleichem Namen "${item.name}" gefunden:`, {
                            existingId: existingProductDoc.id,
                            existingPrice: existingProductData.netPrice
                        });
                        
                        // Wenn Preis oder andere Details unterschiedlich sind, Update vorschlagen
                        const existingPrice = Math.round((existingProductData.netPrice || 0) * 100) / 100;
                        const currentPrice = Math.round((item.netPrice || 0) * 100) / 100;
                        
                        if (Math.abs(existingPrice - currentPrice) > 0.01) {
                            determinedAction = 'update';
                            productIdForAction = existingProductDoc.id;
                            
                            itemsToProcess.push({
                                action: 'update',
                                item,
                                productId: existingProductDoc.id,
                                originalProduct: existingProductData
                            });
                        } else {
                            // Bei identischem Preis einfach die bestehende ID verwenden
                            determinedAction = 'none';
                            productIdForAction = existingProductDoc.id;
                        }
                    } else {
                        // Wirklich ein neues Produkt
                        nativeConsole.log(`Neues Produkt kann erstellt werden für Item ${item.id}:`, {
                            name: item.name,
                            price: item.netPrice
                        });
                        
                        determinedAction = 'create';
                        productIdForAction = null;
                        
                        itemsToProcess.push({
                            action: 'create',
                            item
                        });
                    }
                } catch (error) {
                    nativeConsole.error(`Fehler bei der Suche nach ähnlichen Produkten:`, error);
                    
                    // Im Fehlerfall trotzdem als neues Produkt behandeln
                    nativeConsole.log(`Neues Produkt wird trotz Fehlern erstellt für Item ${item.id}`);
                    determinedAction = 'create';
                    productIdForAction = null;
                    
                    itemsToProcess.push({
                        action: 'create',
                        item
                    });
                }
            }
        }
        
        // Metadaten speichern
        updatedMeta[item.id] = { action: determinedAction, productId: productIdForAction };
    }

    // Update die Metadaten für die Items
    // **FIX: Auch Ref aktualisieren für sofortigen Zugriff**
    const newMeta = { ...productMetaRef.current, ...updatedMeta };
    setProductMetaForItems(newMeta);
    productMetaRef.current = newMeta;
    nativeConsole.log("Updated product meta state:", updatedMeta);

    // WICHTIG: Prüfe, ob das Formular gerade zurückgesetzt wurde/initialisiert wird
    // In diesem Fall sollten wir keinen Dialog zeigen, auch wenn es Änderungen gibt
    const isFormReset = currentItems.every(item => {
        // Ein neues/reset Item hat typischerweise:
        // 1. Eine ID aber keinen Namen ODER
        // 2. Standardwerte wie leere Beschreibungen oder 0 Werte
        return (!item.name || item.name === '') && 
               (!item.description || item.description === '') && 
               (!item.netPrice || item.netPrice === 0);
    });
    
    if (isFormReset) {
        nativeConsole.log("Formularzurücksetzung erkannt, kein Dialog nötig.");
        return true; // Validation erfolgreich - Prozess fortsetzen
    }

    // Dialog nur anzeigen, wenn tatsächlich Aktionen durchzuführen sind
    // UND wir nicht in einem automatischen Prozess sind
    if (itemsToProcess.length > 0) {
        let dialogTitle = '';
        let dialogMessage = '';
        
        if (itemsToProcess.length === 1) {
            const item = itemsToProcess[0];
            if (item.action === 'update') {
                dialogTitle = 'Produkt aktualisieren';
                dialogMessage = `Das Produkt "${item.item.name}" wurde geändert. Möchten Sie das Produkt in der Datenbank aktualisieren?`;
            } else {
                dialogTitle = 'Neues Produkt erstellen';
                dialogMessage = `Möchten Sie das Produkt "${item.item.name}" als neues Produkt in der Datenbank speichern?`;
            }
        } else {
            const updateCount = itemsToProcess.filter(i => i.action === 'update').length;
            const createCount = itemsToProcess.filter(i => i.action === 'create').length;
            
            dialogTitle = 'Produktdaten verarbeiten';
            dialogMessage = `Es wurden Änderungen an ${itemsToProcess.length} Produkten festgestellt:`;
            
            if (updateCount > 0) {
                dialogMessage += ` ${updateCount} zu aktualisierende Produkte.`;
            }
            
            if (createCount > 0) {
                dialogMessage += ` ${createCount} neu zu erstellende Produkte.`;
            }
            
            dialogMessage += ' Möchten Sie diese Änderungen in der Datenbank speichern?';
        }
        
        nativeConsole.log(`Dialog für ${itemsToProcess.length} Produkte wird angezeigt.`, {
            title: dialogTitle,
            itemsCount: itemsToProcess.length,
            actions: itemsToProcess.map(i => `${i.action}: ${i.item.name}`)
        });
        
        openDialog({
            title: dialogTitle,
            message: dialogMessage,
            action: 'process',
            items: currentItems,
            itemsToProcess
        });
        
        // WICHTIG: false zurückgeben, um den Submit zu stoppen
        // Der Dialog-Handler (onProductDialogAction) übernimmt dann den Submit
        return false; // Dialog wurde geöffnet - Submit wird vom Dialog-Handler übernommen
    } else {
        nativeConsole.log("Keine Produktänderungen zum Verarbeiten gefunden.");
        return true; // Validation erfolgreich - Prozess fortsetzen
    }
}, [currentUser, effectiveScopeUid, productMetaForItems, originalProducts, db, openDialog, closeDialog]);

  return {
    productMetaForItems,
    checkAndHandleProductUpdate,
    handleProductSelection,
    resetProductManagement,
    isLoading,
    error,
    // Dialog-related properties
    dialogOpen,
    setDialogOpen,
    dialogConfig,
    handleDialogAction,
    // Neue Funktionen für explizite Dialog-Steuerung
    closeDialogAndContinue: useCallback(() => {
        nativeConsole.log("Closing product dialog and continuing with submission");
        closeDialog();
    }, [closeDialog]),
    // **FIX: Funktion zum Abrufen der aktuellen productMetaForItems aus der Ref**
    getProductMetaForItems,
  };
};
